import React, { useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, FileText, User, Calendar, Clock, Mail, Hash, CheckCircle } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { Client } from '../../types';

interface ServiceAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (agreement: any) => void;
  clients: Client[];
  editingAgreement?: any;
}

const ServiceAgreementModal: React.FC<ServiceAgreementModalProps> = ({
  isOpen,
  onClose,
  onSave,
  clients,
  editingAgreement,
}) => {
  const STORAGE_KEY = useMemo(() => 'draft:service_agreement:v1', []);

  const defaultFormData = useMemo(
    () => ({
      template_name: 'Acuerdo de Servicio Productex',
      contract_date: new Date().toISOString().split('T')[0],
      client_id: '',
      production_days: 30,
      variables: {
        client_name: '',
        client_id: '',
        client_email: '',
        production_days: 30,
      },
    }),
    []
  );

  const agreementToFormData = (agreement: any) => {
    if (!agreement) return defaultFormData;

    const variables = agreement.variables || {};
    return {
      template_name: agreement.template_name ?? defaultFormData.template_name,
      contract_date: agreement.contract_date ?? defaultFormData.contract_date,
      client_id: agreement.client_id ?? '',
      production_days: agreement.production_days ?? variables.production_days ?? defaultFormData.production_days,
      variables: {
        client_name: variables.client_name ?? agreement.client_name ?? '',
        client_id: variables.client_id ?? agreement.client_document ?? '',
        client_email: variables.client_email ?? agreement.client_email ?? '',
        production_days: variables.production_days ?? agreement.production_days ?? defaultFormData.variables.production_days,
      },
    };
  };

  const [formData, setFormData] = useState(() => agreementToFormData(editingAgreement));
  
  const [error, setError] = useState<string | null>(null);
  const didInitOpenRef = useRef(false);
  
  // Reset form when modal opens
  React.useEffect(() => {
    if (isOpen) {
      // Importante: no pisar lo que el usuario ya estaba escribiendo.
      // Si existe un borrador, se restaura. Si se está editando un acuerdo, se carga desde editingAgreement.
      if (editingAgreement) {
        setFormData(agreementToFormData(editingAgreement));
        setError(null);
        didInitOpenRef.current = true;
        return;
      }

      const draftRaw = sessionStorage.getItem(STORAGE_KEY);
      if (draftRaw) {
        try {
          const draft = JSON.parse(draftRaw);
          setFormData({ ...defaultFormData, ...draft, variables: { ...defaultFormData.variables, ...(draft?.variables || {}) } });
          setError(null);
          didInitOpenRef.current = true;
          return;
        } catch {
          // Si el draft se corrompe, se ignora.
        }
      }

      // Solo inicializamos por defecto si no hubo borrador.
      // Además evitamos re-inicializar si el modal se re-monta estando abierto.
      if (!didInitOpenRef.current) {
        setFormData(defaultFormData);
      }
      setError(null);
      didInitOpenRef.current = true;
    }
    if (!isOpen) {
      didInitOpenRef.current = false;
    }
  }, [STORAGE_KEY, defaultFormData, editingAgreement, isOpen]);

  // Persistir borrador mientras el modal está abierto (para navegar de pestaña/ruta y volver).
  React.useEffect(() => {
    if (!isOpen) return;
    if (editingAgreement) return; // no pisar borrador cuando se edita
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    } catch {
      // no-op
    }
  }, [STORAGE_KEY, editingAgreement, formData, isOpen]);
  
  // Update variables when client changes
  React.useEffect(() => {
    if (formData.client_id) {
      const selectedClient = clients.find(c => c.id === formData.client_id);
      if (selectedClient) {
        setFormData(prev => ({
          ...prev,
          variables: {
            ...prev.variables,
            client_name: selectedClient.name,
            client_id: selectedClient.cuit || selectedClient.document || '',
            client_email: selectedClient.email || '',
          }
        }));
      }
    }
  }, [formData.client_id, clients]);
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.client_id) {
      setError('Debe seleccionar un cliente');
      return;
    }
    
    if (!formData.variables.client_name) {
      setError('El nombre del cliente es requerido');
      return;
    }
    
    if (formData.production_days < 1) {
      setError('Los días de producción deben ser mayor a 0');
      return;
    }
    
    // Get client details
    const client = clients.find(c => c.id === formData.client_id);
    if (!client) {
      setError('Cliente no encontrado');
      return;
    }
    
    // Create service agreement object
    const agreement = {
      template_name: formData.template_name,
      contract_date: formData.contract_date,
      client_id: formData.client_id,
      client_name: formData.variables.client_name,
      client_document: formData.variables.client_id,
      client_email: formData.variables.client_email,
      production_days: formData.production_days,
      variables: {
        ...formData.variables,
        production_days: formData.production_days
      },
      generated_by: 'current-user-id',
      generated_by_name: 'Usuario Actual',
      generated_at: new Date().toISOString(),
    };
    
    // Save service agreement
    console.log('[SERVICE_AGREEMENT_MODAL] Calling onSave with:', agreement);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // no-op
    }
    onSave(agreement);
  };
  
  return (
    <Dialog.Root open={isOpen} onOpenChange={onClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-50 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                <FileText className="mr-2 h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                {editingAgreement ? 'Editar Acuerdo' : 'Nuevo Acuerdo de Servicio'}
              </Dialog.Title>
              <Dialog.Close asChild>
                <Button variant="outline" size="sm" icon={<X className="h-4 w-4" />} />
              </Dialog.Close>
            </div>
            
            {error && (
              <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Step 1: Basic Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">1. Información Básica</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nombre del Template <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={formData.template_name}
                      onChange={(e) => setFormData({ ...formData, template_name: e.target.value })}
                      placeholder="Acuerdo de Servicio Productex"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Fecha del Contrato <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="date"
                      value={formData.contract_date}
                      onChange={(e) => setFormData({ ...formData, contract_date: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Step 2: Client Selection */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">2. Selección del Cliente</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cliente <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={formData.client_id}
                    onChange={(value) => setFormData({ ...formData, client_id: value })}
                    options={[
                      { value: '', label: 'Seleccionar cliente...' },
                      ...clients.map(client => ({
                        value: client.id,
                        label: client.name
                      }))
                    ]}
                  />
                </div>
              </div>
              
              {/* Step 3: Contract Variables */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">3. Variables del Contrato</h3>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Nombre del Cliente <span className="text-red-500">*</span>
                      </label>
                      <Input
                        value={formData.variables.client_name}
                        onChange={(e) => setFormData({
                          ...formData,
                          variables: { ...formData.variables, client_name: e.target.value }
                        })}
                        placeholder="Nombre completo del cliente"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Documento (CUIT/CUIL/DNI)
                      </label>
                      <Input
                        value={formData.variables.client_id}
                        onChange={(e) => setFormData({
                          ...formData,
                          variables: { ...formData.variables, client_id: e.target.value }
                        })}
                        placeholder="20-12345678-9"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Email del Cliente
                      </label>
                      <Input
                        type="email"
                        value={formData.variables.client_email}
                        onChange={(e) => setFormData({
                          ...formData,
                          variables: { ...formData.variables, client_email: e.target.value }
                        })}
                        placeholder="cliente@ejemplo.com"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Días de Producción <span className="text-red-500">*</span>
                      </label>
                      <Input
                        type="number"
                        min="1"
                        value={formData.production_days}
                        onChange={(e) => setFormData({ ...formData, production_days: parseInt(e.target.value) || 30 })}
                        placeholder="30"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              
              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  icon={<FileText className="h-4 w-4" />}
                >
                  {editingAgreement ? 'Actualizar Acuerdo' : 'Generar Acuerdo'}
                </Button>
              </div>
            </form>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default ServiceAgreementModal;
